import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeftIcon, CheckIcon, PencilIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { Building2, AlertTriangle, CheckCircle } from 'lucide-react'
import { Button, Input } from '../../components/ui'
import Badge from '../../components/ui/Badge'
import FileDropzone from '../../components/ui/FileDropzone'
import VendorDocumentList from '../../components/vendor/VendorDocumentList'
import { apiClient } from '../../lib/api'
import { vendorDocumentsAPI, observationsAPI } from '../../lib/services'

// ── Types ──────────────────────────────────────────────────────────

interface SpecItem {
  id: string
  item_id: number
  item_title: string
  spec_name: string | null
  spec_type: string | null
  spec_unit: string | null
  spec_id: number | null
  has_data: boolean
  current_value?: string | null
  section_title: string
  scorecard_name: string
  scorecard_type: string
  description: string
}

interface OpportunityDetail {
  id: number
  program_id: number
  program_name: string | null
  office_name: string | null
  status: string
  recommendation_type: string
  recommended_at: string | null
  vendor_id: number
  vendor_name: string | null
  product_id: number | null
  product_name: string | null
  product_description: string | null
  missing_vendor_items: SpecItem[]
  missing_product_items: SpecItem[]
  complete_product_items: SpecItem[]
}

// ── Helpers ────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  recommended: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  invited: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
  under_consideration: 'bg-purple-500/10 text-purple-600 border-purple-500/20',
  selected: 'bg-green-500/10 text-green-600 border-green-500/20',
  contracted: 'bg-green-600/10 text-green-700 border-green-600/20',
  declined: 'bg-muted text-muted-foreground border-border',
  rejected: 'bg-red-500/10 text-red-600 border-red-500/20',
}

// ── Component ──────────────────────────────────────────────────────

export default function OpportunityResponse() {
  const { opportunityId } = useParams<{ opportunityId: string }>()
  const navigate = useNavigate()
  const [opportunity, setOpportunity] = useState<OpportunityDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [docRefresh, setDocRefresh] = useState(0)

  const fetchOpportunity = useCallback(async () => {
    if (!opportunityId) return
    try {
      const data = await apiClient.get<OpportunityDetail>(`/api/vendor-opportunities/${opportunityId}/`)
      setOpportunity(data)
    } catch (err: any) {
      setError(err.message || 'Failed to load opportunity')
    } finally {
      setLoading(false)
    }
  }, [opportunityId])

  useEffect(() => {
    fetchOpportunity()
  }, [fetchOpportunity])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-muted-foreground">Loading opportunity...</div>
      </div>
    )
  }

  if (error || !opportunity) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-red-500">{error || 'Opportunity not found'}</div>
      </div>
    )
  }

  return (
    <div className="max-w-5xl mx-auto px-6 sm:px-10 py-8">
      {/* Back button */}
      <button
        onClick={() => navigate('/submit')}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
      >
        <ArrowLeftIcon className="w-4 h-4" />
        Back to Profile
      </button>

      {/* Program Header */}
      <div className="border border-border rounded-lg p-6 mb-8">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
              You've been invited to submit more information
            </p>
            <h1 className="text-xl font-bold text-foreground">{opportunity.program_name}</h1>
            {opportunity.office_name && (
              <div className="flex items-center gap-1.5 mt-1">
                <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">{opportunity.office_name}</span>
              </div>
            )}
          </div>
          <span className={`inline-flex px-2.5 py-1 text-xs rounded-full border ${STATUS_COLORS[opportunity.status] || 'bg-muted text-muted-foreground border-border'}`}>
            {opportunity.status.replace(/_/g, ' ')}
          </span>
        </div>

        {/* Product under consideration */}
        {opportunity.product_name && (
          <div className="mt-4 pt-4 border-t border-border">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Product Under Consideration</p>
            <p className="text-base font-semibold text-foreground">{opportunity.product_name}</p>
            {opportunity.product_description && (
              <p className="text-sm text-muted-foreground mt-1 line-clamp-3">{opportunity.product_description}</p>
            )}
          </div>
        )}
      </div>

      {/* Request section - custom message from PM (placeholder) */}
      <div className="border border-border rounded-lg p-6 mb-8">
        <h2 className="text-base font-semibold text-foreground mb-3">Request</h2>
        <div className="bg-muted/30 rounded-lg p-4">
          <p className="text-sm text-foreground leading-relaxed">
            Please review the missing information below and upload any supporting documents (datasheets, spec sheets, company documents)
            to help us evaluate your submission for <strong>{opportunity.program_name}</strong>.
          </p>
        </div>
      </div>

      {/* Documents section */}
      {opportunity.product_id && (
        <div className="border border-border rounded-lg p-6 mb-8">
          <h2 className="text-base font-semibold text-foreground mb-2">Documents</h2>
          <p className="text-xs text-muted-foreground mb-4">
            Upload tech specs or datasheets — we'll automatically extract relevant information.
          </p>
          <FileDropzone
            onFilesSelected={async (files) => {
              for (const file of files) {
                try {
                  await vendorDocumentsAPI.upload(
                    file,
                    opportunity.vendor_id,
                    'datasheet',
                    opportunity.product_id!
                  )
                } catch (err) {
                  console.error('Failed to upload document:', err)
                }
              }
              setDocRefresh(n => n + 1)
            }}
            label="Drop datasheets, spec sheets, or company documents here"
          />
          <VendorDocumentList
            vendorId={opportunity.vendor_id}
            productId={opportunity.product_id}
            refreshTrigger={docRefresh}
            onSpecsExtracted={() => {
              fetchOpportunity()
              setDocRefresh(n => n + 1)
            }}
          />
        </div>
      )}

      {/* Missing Information Tables */}
      {opportunity.missing_product_items.length > 0 && (
        <div className="border border-amber-500/20 rounded-lg mb-8 overflow-hidden">
          <div className="bg-amber-500/5 px-6 py-3 border-b border-amber-500/20 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            <h2 className="text-sm font-semibold text-foreground">Missing Product Details</h2>
            <Badge variant="warning" className="text-[10px] ml-auto">
              {opportunity.missing_product_items.length} items
            </Badge>
          </div>
          <SpecItemTable
            items={opportunity.missing_product_items}
            entityType="product"
            entityId={opportunity.product_id}
            onUpdate={fetchOpportunity}
          />
        </div>
      )}

      {opportunity.missing_vendor_items.length > 0 && (
        <div className="border border-amber-500/20 rounded-lg mb-8 overflow-hidden">
          <div className="bg-amber-500/5 px-6 py-3 border-b border-amber-500/20 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            <h2 className="text-sm font-semibold text-foreground">Missing Company Details</h2>
            <Badge variant="warning" className="text-[10px] ml-auto">
              {opportunity.missing_vendor_items.length} items
            </Badge>
          </div>
          <SpecItemTable
            items={opportunity.missing_vendor_items}
            entityType="vendor"
            entityId={opportunity.vendor_id}
            onUpdate={fetchOpportunity}
          />
        </div>
      )}

      {opportunity.complete_product_items.length > 0 && (
        <div className="border border-border rounded-lg mb-8 overflow-hidden">
          <div className="bg-success/5 px-6 py-3 border-b border-border flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-success" />
            <h2 className="text-sm font-semibold text-foreground">Complete Details</h2>
            <Badge variant="info" className="text-[10px] ml-auto">
              {opportunity.complete_product_items.length} items
            </Badge>
          </div>
          <SpecItemTable
            items={opportunity.complete_product_items}
            entityType="product"
            entityId={opportunity.product_id}
            onUpdate={fetchOpportunity}
            isComplete
          />
        </div>
      )}

      {/* No missing items state */}
      {opportunity.missing_product_items.length === 0 &&
       opportunity.missing_vendor_items.length === 0 &&
       opportunity.complete_product_items.length === 0 && (
        <div className="border border-border rounded-lg p-8 text-center mb-8">
          <p className="text-sm text-muted-foreground">
            No scorecard evaluation data available yet. Upload documents above or check back later.
          </p>
        </div>
      )}
    </div>
  )
}

// ── Spec Item Table ────────────────────────────────────────────────

function SpecItemTable({
  items,
  entityType,
  entityId,
  onUpdate,
  isComplete = false,
}: {
  items: SpecItem[]
  entityType: 'product' | 'vendor'
  entityId: number | null
  onUpdate: () => void
  isComplete?: boolean
}) {
  // Group items by section
  const sections = new Map<string, SpecItem[]>()
  for (const item of items) {
    const key = item.section_title || 'Other'
    if (!sections.has(key)) sections.set(key, [])
    sections.get(key)!.push(item)
  }

  return (
    <div className="divide-y divide-border/50">
      {Array.from(sections.entries()).map(([sectionTitle, sectionItems]) => (
        <div key={sectionTitle}>
          <div className="px-6 py-2 bg-muted/20">
            <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
              {sectionTitle}
            </span>
          </div>
          <div className="divide-y divide-border/30">
            {sectionItems.map(item => (
              <SpecItemRow
                key={item.id}
                item={item}
                entityType={entityType}
                entityId={entityId}
                onUpdate={onUpdate}
                isComplete={isComplete}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

// ── Single Spec Item Row ───────────────────────────────────────────

function SpecItemRow({
  item,
  entityType,
  entityId,
  onUpdate,
  isComplete,
}: {
  item: SpecItem
  entityType: 'product' | 'vendor'
  entityId: number | null
  onUpdate: () => void
  isComplete: boolean
}) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState('')
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    if (!item.spec_id || !entityId) return
    setSaving(true)
    try {
      const data: Record<string, unknown> = {
        entity_type: entityType,
        spec: item.spec_id,
        confidence: 5,
        observed_date: new Date().toISOString().split('T')[0],
      }
      if (entityType === 'product') {
        data.product = entityId
      } else {
        data.vendor = entityId
      }

      if (item.spec_type === 'numeric') {
        data.value_numeric = parseFloat(value)
      } else if (item.spec_type === 'boolean') {
        data.value_boolean = value === 'true'
      } else {
        data.value_text = value
      }

      await observationsAPI.create(data)
      setEditing(false)
      setValue('')
      onUpdate()
    } catch (err) {
      console.error('Failed to save:', err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="px-6 py-3 flex items-center gap-3 hover:bg-muted/20 transition-colors">
      {/* Status indicator */}
      <div className="flex-shrink-0">
        {item.has_data ? (
          <CheckCircle className="w-4 h-4 text-success" />
        ) : (
          <div className="w-4 h-4 rounded-full border-2 border-amber-500/50" />
        )}
      </div>

      {/* Spec info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-foreground truncate">
            {item.item_title}
          </span>
          {item.spec_unit && (
            <span className="text-[10px] text-muted-foreground flex-shrink-0">
              ({item.spec_unit})
            </span>
          )}
        </div>
        {item.description && (
          <p className="text-xs text-muted-foreground mt-0.5 truncate">{item.description}</p>
        )}
      </div>

      {/* Value / Input area */}
      <div className="flex-shrink-0 flex items-center gap-2">
        {editing ? (
          <div className="flex items-center gap-1.5">
            {item.spec_type === 'text' ? (
              <textarea
                value={value}
                onChange={e => setValue(e.target.value)}
                className="w-48 h-16 p-2 text-xs border border-border rounded bg-background text-foreground resize-none"
                autoFocus
                placeholder="Enter value..."
              />
            ) : item.spec_type === 'boolean' ? (
              <select
                value={value}
                onChange={e => setValue(e.target.value)}
                className="h-8 px-2 text-xs border border-border rounded bg-background text-foreground"
                autoFocus
              >
                <option value="">Select...</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            ) : (
              <div className="flex items-center gap-1">
                <Input
                  value={value}
                  onChange={e => setValue(e.target.value)}
                  type="number"
                  className="h-8 w-24 text-xs"
                  autoFocus
                  placeholder="Value"
                />
                {item.spec_unit && (
                  <span className="text-xs text-muted-foreground">{item.spec_unit}</span>
                )}
              </div>
            )}
            <Button variant="ghost" size="sm" onClick={handleSave} disabled={saving || !value} className="h-7 px-1.5">
              <CheckIcon className="w-3.5 h-3.5" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { setEditing(false); setValue('') }} className="h-7 px-1.5">
              <XMarkIcon className="w-3.5 h-3.5" />
            </Button>
          </div>
        ) : (
          <>
            {isComplete && item.current_value && (
              <span className="text-xs text-success font-medium">
                {item.current_value}{item.spec_unit ? ` ${item.spec_unit}` : ''}
              </span>
            )}
            {isComplete && !item.current_value && (
              <span className="text-xs text-success font-medium">Provided</span>
            )}
            {item.spec_id && entityId && (
              <button
                onClick={() => setEditing(true)}
                className="p-1 hover:bg-muted rounded transition-colors"
                title={isComplete ? 'Update value' : 'Add value'}
              >
                <PencilIcon className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
            )}
          </>
        )}
      </div>
    </div>
  )
}
